import { TFunction } from "i18next"
import { colors } from "../config/colors"
import months from "../config/months"
import { capitalizeFirstCharacter, formatNumber, roundWithDecimalPlaces } from "./utils"

export interface MobilityChartItem {
  id: string
  label: string
  value: number
  color: string
}

// label for a mode of transport (e.g. "walk", "bicycle")
export const getTransportModeLabel = (t: TFunction, type: string) => {
  return t(`transport_mode_${type}`, {
    defaultValue: capitalizeFirstCharacter(type.replace(/_/g, " ")),
  }) as string
}

// color for a mode of transport, falling back to a fixed palette for unknown modes
export const getTransportModeColor = (type: string, index: number) => {
  const color = (colors.mobility as any)[type]
  if (typeof color === "string") {
    return color
  }

  return colors.mobility.fallback[index % colors.mobility.fallback.length]
}

// sort items by value and keep the first "max" ones, grouping the rest into "Others"
export const groupTopItems = (
  items: MobilityChartItem[],
  max: number,
  othersLabel: string
) => {
  const sorted = items
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value)

  if (sorted.length <= max) {
    return sorted
  }

  const others = sorted
    .slice(max)
    .reduce((sum, item) => sum + item.value, 0)

  return [
    ...sorted.slice(0, max),
    {
      id: "others",
      label: othersLabel,
      value: others,
      color: colors.mobility.others,
    },
  ]
}

// format a number with at most "decimalPlaces" decimals
export const formatMobilityNumber = (
  n: number | null | undefined,
  decimalPlaces = 1
) => {
  return formatNumber(roundWithDecimalPlaces(n ?? 0, decimalPlaces))
}

// "YYYY-MM-DD" date parts, without time zone shifts
const getDateParts = (date: string) => {
  const [year, month, day] = date.split("T")[0].split("-").map(Number)
  return { year, month: month - 1, day }
}

// label for a month (e.g. "Sep 2026")
export const getMonthLabel = (t: TFunction, date: string) => {
  const { year, month } = getDateParts(date)
  return `${capitalizeFirstCharacter(
    (t(months[month]) as string).slice(0, 3)
  )} ${year}`
}

// label for a day (e.g. "7 Sep 2026")
export const getDayLabel = (t: TFunction, date: string) => {
  const { year, month, day } = getDateParts(date)
  return `${day} ${capitalizeFirstCharacter(
    (t(months[month]) as string).slice(0, 3)
  )} ${year}`
}

// colour scale from a light tint of the primary colour to the primary colour
const hexToRgb = (hex: string) => {
  const value = parseInt(hex.replace("#", ""), 16)
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255]
}
const scaleStart = hexToRgb(colors.strokeLight)
const scaleEnd = hexToRgb(colors.primary)
// colour of a country on the users map (and its swatch in the list): value / max users
export const getUsersMapColor = (normalizedValue: number) => {
  // square root, so a single big country doesn't flatten all the others, starting
  // a bit above the tint so countries with few users stand out from the grey ones
  const t = 0.15 + 0.85 * Math.sqrt(Math.min(Math.max(normalizedValue, 0), 1))
  const [r, g, b] = scaleStart.map((start, index) =>
    Math.round(start + (scaleEnd[index] - start) * t)
  )
  return `rgb(${r}, ${g}, ${b})`
}
